import { Alert, Button, Col, Form, Input, message, Modal, notification, Row, Upload } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import { filterDigits, localize } from 'mixins/helpers';
import React, { useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import { getActiveUser } from '../../../../../react4xp/parts/admin-header/actions';
import { IUser } from '../../../../../react4xp/parts/admin-users/admin-users.type';
import { sendEmail } from '/react4xp/parts/main/actions';
import { EmailSource } from '/react4xp/parts/main/actions.types';

const ContactsModal = (props: any) => {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [form] = Form.useForm();
    const { TextArea } = Input;

    const [activeUser, setActiveUser] = useState<IUser | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!props.token || !props.isModalOpen) return;

        getActiveUser()
            .then((res) => res.data)
            .then((res) => {
                setActiveUser(res);

                form.setFieldsValue({
                    email: res?.email || '',
                    name: res?.fullname || '',
                    identifier: res?.login || '',
                });
            })
            .catch(() => {
                message.error('Не удалось получить данные пользователя');
            });
    }, [props.token, props.isModalOpen, form]);

    const handleFinish = async (values: any) => {
        try {
            setSubmitting(true);

            const formData = new FormData();

            formData.append(
                'title',
                props.source === EmailSource.GlobalApi
                    ? localize('global-api-access-title', props.translations)
                    : values.title
            );
            formData.append('identifier', values.identifier || activeUser?.login || '');
            formData.append('content', values.content);
            formData.append('name', values.name || activeUser?.fullname || '');
            formData.append('phoneNumber', values.phoneNumber);
            formData.append('email', values.email);
            formData.append('source', props.source);

            if (fileList.length > 0) {
                fileList.forEach((file) => {
                    if (file.originFileObj) {
                        formData.append('files', file.originFileObj);
                    }
                });
            }

            await sendEmail(formData);

            notification.success({
                message: 'Успешно',
            });

            setActiveUser((prev) =>
                prev
                    ? {
                          ...prev,
                          email: values.email,
                          fullname: values.name || prev.fullname,
                          login: values.identifier || prev.login,
                      }
                    : prev
            );

            setFileList([]);

            form.resetFields(['phoneNumber', 'title', 'content', 'file']);

            form.setFieldsValue({
                email: values.email,
                name: values.name || activeUser?.fullname || '',
                identifier: values.identifier || activeUser?.login || '',
            });

            props.handleOk();
        } catch (error) {
            notification.error({
                message: 'Ошибка',
                description: 'Не удалось отправить обращение',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        props.handleCancel();
    };

    const uploadProps = {
        onRemove: (file: UploadFile) => {
            const newFileList = fileList.filter((item) => item.uid !== file.uid);
            setFileList(newFileList);
        },
        beforeUpload: (file: File) => {
            const isAllowedType = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'image/jpeg',
                'image/png',
            ].includes(file.type);

            if (!isAllowedType) {
                message.error('Можно загружать только файлы PDF, Word, Excel и JPEG!');
                return Upload.LIST_IGNORE;
            }

            const isLessThan10MB = file.size / 1024 / 1024 < 10;
            if (!isLessThan10MB) {
                message.error('Файл должен быть меньше 10MB!');
                return Upload.LIST_IGNORE;
            }

            setFileList([file as unknown as UploadFile]);
            return false;
        },
        fileList,
    };

    return (
        <Modal
            title={props.title || props.config?.contactTitle}
            open={props.isModalOpen}
            onOk={() => form.submit()}
            onCancel={handleCancel}
            confirmLoading={submitting}
            width="70%"
            okText={localize('button.send', props.translations)}
        >
            {props.config?.isShowContactInfoText && (
                <Alert
                    message={
                        <div dangerouslySetInnerHTML={{ __html: props.config?.contactInfoText }} />
                    }
                    type="info"
                    className="my-3"
                />
            )}

            <Form
                form={form}
                layout="vertical"
                className="my-3"
                onFinish={handleFinish}
            >
                <Row gutter={15}>
                    <Col span={12}>
                        <Form.Item
                            name="name"
                            label={localize('fio-or-org-name', props.translations)}
                            rules={[
                                {
                                    required: true,
                                    message: localize(
                                        'block-auth-required-field',
                                        props.translations
                                    ),
                                },
                            ]}
                        >
                            <Input disabled />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="phoneNumber"
                            label={localize('placeholder.phone', props.translations)}
                            rules={[
                                {
                                    required: true,
                                    message: localize(
                                        'block-auth-required-field',
                                        props.translations
                                    ),
                                },
                            ]}
                        >
                            <InputMask mask="+7 999 999 99 99">
                                {
                                    // @ts-ignore
                                    (inputProps: any) => (
                                        <Input
                                            placeholder="+7 777 777 77 77"
                                            {...inputProps}
                                        />
                                    )
                                }
                            </InputMask>
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="email"
                            label={localize('email', props.translations)}
                            rules={[
                                {
                                    required: true,
                                    message: localize(
                                        'block-auth-required-field',
                                        props.translations
                                    ),
                                },
                                {
                                    type: 'email',
                                    message: localize('error.email-format', props.translations),
                                },
                            ]}
                        >
                            <Input
                                placeholder={localize('placeholder.email', props.translations)}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        {props.source !== EmailSource.GlobalApi ? (
                            <Form.Item
                                name="title"
                                label={localize('theme', props.translations)}
                                rules={[
                                    {
                                        required: true,
                                        message: localize(
                                            'block-auth-required-field',
                                            props.translations
                                        ),
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                name="identifier"
                                label={localize('iin-bin', props.translations)}
                                rules={[
                                    {
                                        required: true,
                                        message: localize(
                                            'block-auth-required-field',
                                            props.translations
                                        ),
                                    },
                                    {
                                        min: 12,
                                        message: localize(
                                            'error.iin-bin-length-12',
                                            props.translations
                                        ),
                                    },
                                ]}
                            >
                                <Input
                                    onChange={filterDigits(form, 'identifier')}
                                    maxLength={12}
                                    disabled
                                />
                            </Form.Item>
                        )}
                    </Col>

                    <Col span={24}>
                        <Form.Item
                            name="content"
                            label={localize('message', props.translations)}
                            rules={[
                                {
                                    required: true,
                                    message: localize(
                                        'block-auth-required-field',
                                        props.translations
                                    ),
                                },
                            ]}
                        >
                            <TextArea rows={4} />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item
                            name="file"
                            label={localize('attach-file-label', props.translations)}
                        >
                            <Upload
                                {...uploadProps}
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.png"
                            >
                                <Button>{localize('select-file', props.translations)}</Button>
                            </Upload>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default ContactsModal;